import { useState } from "react";
import { Icon } from "./Icon";
import { useEscape } from "./ActivityModal";
import { useI18n } from "../lib/i18n";
import { emptyLearnerProfile } from "../lib/data";
import { EnrolmentChoice } from "./EnrolmentChoice";
import { WEEKLY_BLOCK_TOTAL, blockTotal } from "../lib/pathways";
import type { Level } from "../lib/curriculum";
import type { ServiceRequestOutcome } from "../lib/data";
import type { EnrolmentStatus, Learner, LearnerProfileInput } from "../types";

const COLORS = ["blue", "mint", "rose", "gold", "lavender", "green"];

/** Stage ids. Their labels live in the dictionary under `stage.<id>`. */
export const STAGES = ["foundation", "primary", "lower-secondary"];

const GENDERS = ["female", "male", "unspecified"];
const ENROLMENT: EnrolmentStatus[] = ["active", "paused", "completed"];

/**
 * A child's profile: the four things needed to start, and the rest when the
 * family is ready to give it.
 *
 * The split is the whole design. A parent adding their first child at nine in
 * the evening should be able to have tomorrow's plan built in thirty seconds,
 * so the required part is a name. Everything else — what this child is like,
 * what they need, what the family is aiming at — is what a coach would
 * otherwise have to ask for twice, so it is here and it is optional, behind one
 * disclosure rather than on a second screen nobody returns to.
 */
export function LearnerForm({
  firstChild,
  learner,
  levels,
  onClose,
  onSave,
}: {
  firstChild: boolean;
  /** Set when editing; absent when adding. */
  learner?: Learner | null;
  /** Playgroup through Class 5, in order. */
  levels: Level[];
  onClose: () => void;
  /**
   * Saves the child and returns what that did to their matching request.
   *
   * `void` is still accepted so a caller with nothing to report — a teacher or
   * admin screen editing a profile — need not invent an outcome.
   */
  onSave: (input: LearnerProfileInput) => Promise<ServiceRequestOutcome | void>;
}) {
  const { t, locale } = useI18n();
  useEscape(onClose);

  const [draft, setDraft] = useState<LearnerProfileInput>(() => {
    if (!learner) return emptyLearnerProfile();
    const { id, guardian_id, profile_id, initials, is_active, ...profile } = learner;
    void id;
    void guardian_id;
    void profile_id;
    void initials;
    void is_active;
    return profile;
  });
  // Someone editing has already met the app; someone adding their first child
  // has not, and a wall of optional fields is the fastest way to lose them.
  const [expanded, setExpanded] = useState(Boolean(learner) && !firstChild);
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState("");

  function update<K extends keyof LearnerProfileInput>(key: K, value: LearnerProfileInput[K]) {
    setDraft((current) => ({ ...current, [key]: value }));
  }

  /** Blank optional text is stored as null, not "", so "has this been filled in?" has one answer. */
  function text(key: keyof LearnerProfileInput) {
    return (draft[key] as string | null) ?? "";
  }
  function setText(key: keyof LearnerProfileInput, value: string) {
    update(key, (value.trim() ? value : null) as LearnerProfileInput[typeof key]);
  }

  async function submit() {
    if (saving) return;
    const name = draft.name.trim();
    if (!name) {
      setProblem(t("learner.needName"));
      return;
    }
    if (!draft.level_slug) {
      setProblem(t("learner.needLevel"));
      return;
    }
    if (!draft.learning_route) {
      setProblem(t("learner.needRoute"));
      return;
    }
    if (draft.learning_route !== "parent_led" && !draft.curriculum_pathway) {
      setProblem(t("learner.needPathway"));
      return;
    }
    if (draft.learning_route !== "parent_led" && !draft.preferred_delivery) {
      setProblem(t("learner.needDelivery"));
      return;
    }
    // No subject check any more: subjects are not chosen at enrolment. A
    // parent-led family asks for a tutor from the curriculum page, per subject,
    // once they know which part they actually need help with.
    if (blockTotal(draft.pathway_blocks ?? []) > WEEKLY_BLOCK_TOTAL) {
      setProblem(t("learner.blocksOverAllocated", { total: WEEKLY_BLOCK_TOTAL }));
      return;
    }
    setSaving(true);
    setProblem("");
    try {
      const request = await onSave({ ...draft, name });
      // The child's details are saved either way. What could not change is the
      // matching request, because a coordinator is already working it — and a
      // family told nothing would reasonably assume their new pathway had been
      // taken, and wait for a teacher who was never going to be arranged.
      if (request?.kind === "locked") {
        setProblem(t("learner.requestLocked"));
        setSaving(false);
        return;
      }
      onClose();
    } catch (cause) {
      setProblem(cause instanceof Error ? cause.message : t("error.generic"));
      setSaving(false);
    }
  }

  const title = learner
    ? t("learner.editTitle", { name: learner.name })
    : firstChild
      ? t("learner.whoTitle")
      : t("learner.addTitle");

  return (
    <div className="modal-layer" role="dialog" aria-modal="true" aria-label={title}>
      <div className="modal-card evidence-modal glass learner-modal">
        {(!firstChild || learner) && (
          <button className="modal-close" onClick={onClose} aria-label={t("common.close")}>
            <Icon name="close" />
          </button>
        )}
        <span className="eyebrow">
          {learner ? t("learner.profile") : firstChild ? t("learner.welcome") : t("learner.newLearner")}
        </span>
        <h2>{title}</h2>
        <p>{learner ? t("learner.editCopy") : firstChild ? t("learner.whoCopy") : t("learner.addCopy")}</p>

        <label htmlFor="learner-name">{t("learner.name")}</label>
        <input
          id="learner-name"
          value={draft.name}
          onChange={(event) => update("name", event.target.value)}
          placeholder="Ayesha Khan"
          autoFocus={!learner}
        />

        {/* The level, not the stage. A parent knows their child is in Nursery
            long before they know what a "foundation stage" is — and the level
            is what decides which curriculum the child actually walks. The stage
            is still stored, because the mastery map and the legacy activity
            pool key on it, but it is derived here rather than asked for. */}
        <label>{t("learner.level")}</label>
        <p className="field-hint">{t("learner.levelHint")}</p>
        <div className="evidence-options level-options" role="group" aria-label={t("learner.level")}>
          {levels.map((option) => (
            <button
              key={option.slug}
              type="button"
              aria-pressed={draft.level_slug === option.slug}
              className={draft.level_slug === option.slug ? "selected" : ""}
              onClick={() => {
                update("level_slug", option.slug);
                update("stage", option.stage);
                update("pathway_blocks", null);
                if (draft.level_slug !== option.slug || !draft.level_started_on) {
                  update("level_started_on", new Date().toISOString().slice(0, 10));
                }
              }}
              title={option.subtitle ?? undefined}
            >
              {locale === "ur" ? t(`level.${option.slug}.title`) : option.title}
              {option.subtitle ? <small>{locale === "ur" ? t(`level.${option.slug}.ages`) : option.subtitle}</small> : null}
            </button>
          ))}
        </div>

        <EnrolmentChoice
          earlyYears={["playgroup", "nursery", "kg"].includes(draft.level_slug ?? "")}
          route={draft.learning_route}
          pathway={draft.curriculum_pathway}
          delivery={draft.preferred_delivery}
          onRoute={(learning_route) => {
            update("learning_route", learning_route);
            if (learning_route === "parent_led") {
              update("curriculum_pathway", "balanced");
              update("preferred_delivery", null);
            }
          }}
          onPathway={(curriculum_pathway) => update("curriculum_pathway", curriculum_pathway)}
          onDelivery={(preferred_delivery) => update("preferred_delivery", preferred_delivery)}
          blocks={draft.pathway_blocks}
          onBlocks={(pathway_blocks) => update("pathway_blocks", pathway_blocks)}
        />

        <p className="field-hint">{t("learner.choiceReview")}</p>

        <label htmlFor="learner-birthday">{t("learner.birthday")}</label>
        <input
          id="learner-birthday"
          type="date"
          value={text("date_of_birth")}
          onChange={(event) => setText("date_of_birth", event.target.value)}
        />

        <label>{t("learner.colour")}</label>
        <div className="evidence-options">
          {COLORS.map((option) => (
            <button
              key={option}
              className={draft.avatar_color === option ? "selected" : ""}
              onClick={() => update("avatar_color", option)}
            >
              <span className={`icon-tile ${option}`} style={{ width: 22, height: 22 }} aria-label={option} />
            </button>
          ))}
        </div>

        {!expanded && (
          <button className="link-button" onClick={() => setExpanded(true)}>
            <Icon name="plus" size={14} /> {t("learner.moreDetail")}
          </button>
        )}

        {expanded && (
          <>
            <p className="section-rule">{t("learner.aboutSection")}</p>

            <label>{t("learner.gender")}</label>
            <div className="evidence-options">
              {GENDERS.map((option) => (
                <button
                  key={option}
                  className={draft.gender === option ? "selected" : ""}
                  onClick={() => update("gender", draft.gender === option ? null : option)}
                >
                  {t(`learner.gender.${option}`)}
                </button>
              ))}
            </div>

            <label htmlFor="learner-language">{t("learner.homeLanguage")}</label>
            <input
              id="learner-language"
              value={text("home_language")}
              onChange={(event) => setText("home_language", event.target.value)}
              placeholder="Urdu, English"
            />

            <label htmlFor="learner-city">{t("learner.city")}</label>
            <input
              id="learner-city"
              value={text("city")}
              onChange={(event) => setText("city", event.target.value)}
              placeholder="Lahore"
            />

            <label htmlFor="learner-country">{t("learner.country")}</label>
            <input
              id="learner-country"
              value={text("country")}
              onChange={(event) => setText("country", event.target.value)}
              placeholder="Pakistan"
            />

            <label htmlFor="learner-about">{t("learner.about")}</label>
            <textarea
              id="learner-about"
              value={text("about")}
              onChange={(event) => setText("about", event.target.value)}
              placeholder={t("learner.aboutHint")}
            />

            <label htmlFor="learner-support">{t("learner.support")}</label>
            <textarea
              id="learner-support"
              value={text("support_needs")}
              onChange={(event) => setText("support_needs", event.target.value)}
              placeholder={t("learner.supportHint")}
            />

            <label htmlFor="learner-schooling">{t("learner.schooling")}</label>
            <textarea
              id="learner-schooling"
              value={text("previous_schooling")}
              onChange={(event) => setText("previous_schooling", event.target.value)}
              placeholder={t("learner.schoolingHint")}
            />

            <p className="section-rule">{t("learner.planSection")}</p>

            <label htmlFor="learner-goals">{t("learner.goals")}</label>
            <textarea
              id="learner-goals"
              value={text("goals")}
              onChange={(event) => setText("goals", event.target.value)}
              placeholder={t("learner.goalsHint")}
            />

            <label htmlFor="learner-started">{t("learner.startedOn")}</label>
            <input
              id="learner-started"
              type="date"
              value={text("started_on")}
              onChange={(event) => setText("started_on", event.target.value)}
            />

            <label htmlFor="learner-target">{t("learner.weeklyTarget")}</label>
            <input
              id="learner-target"
              type="number"
              min={0}
              max={3000}
              step={30}
              value={draft.weekly_target_minutes}
              onChange={(event) =>
                update("weekly_target_minutes", Math.max(0, Number(event.target.value) || 0))
              }
            />
            <p className="field-hint">{t("learner.weeklyTargetHint")}</p>

            {learner && (
              <>
                <label>{t("learner.enrolment")}</label>
                <div className="evidence-options">
                  {ENROLMENT.map((option) => (
                    <button
                      key={option}
                      className={draft.enrolment_status === option ? "selected" : ""}
                      onClick={() => update("enrolment_status", option)}
                    >
                      {t(`learner.enrolment.${option}`)}
                    </button>
                  ))}
                </div>
              </>
            )}
          </>
        )}

        {problem && <p className="form-problem">{problem}</p>}

        <div className="modal-actions">
          {(!firstChild || learner) && (
            <button className="soft-button" onClick={onClose} disabled={saving}>
              {t("common.cancel")}
            </button>
          )}
          <button className="primary" onClick={() => void submit()} disabled={saving}>
            <Icon name="check" />{" "}
            {saving ? t("evidence.saving") : learner ? t("common.save") : t("learner.start")}
          </button>
        </div>
      </div>
    </div>
  );
}
